
import { Lesson, UserProgress } from "@/types/lesson";
import { LessonCard } from "./LessonCard";
import { LessonsGridSkeleton } from "./LessonsGridSkeleton";
import { LessonsGridError } from "./LessonsGridError";
import { LessonsGridEmpty } from "./LessonsGridEmpty";
import { LessonsGridHeader } from "./LessonsGridHeader";
import { useModuleDetails, useLessons, useUserProgress } from "@/hooks/use-lessons-data";

interface LessonsGridProps {
  pathId: string;
  onSelectLesson: (lesson: Lesson) => void;
  onBack: () => void;
}

/**
 * LessonsGrid component - displays all lessons for a specific module
 * Fetches lessons, user progress, and module details to show lesson cards with progress tracking
 * Now refactored into smaller, focused components for better maintainability
 */
export const LessonsGrid = ({ pathId, onSelectLesson, onBack }: LessonsGridProps) => {
  // Fetch module details for debugging and display
  const { data: moduleDetails } = useModuleDetails(pathId);

  // Fetch lessons with enhanced debugging and error handling
  const { data: lessons = [], isLoading: isLoadingLessons, error: lessonsError } = useLessons(pathId);

  // Fetch user progress with better error handling
  const { data: userProgress = [], isLoading: isLoadingProgress } = useUserProgress(pathId);

  // Handle loading states
  if (isLoadingLessons || isLoadingProgress) {
    console.log("⏳ Loading lessons or progress...");
    return <LessonsGridSkeleton />;
  }

  // Handle errors
  if (lessonsError) {
    return <LessonsGridError onBack={onBack} pathId={pathId} error={lessonsError} />;
  }

  /**
   * Get progress data for a specific lesson
   * @param lessonId - The lesson ID to get progress for
   * @returns UserProgress object or undefined
   */
  const getProgressForLesson = (lessonId: string) => {
    const progress = userProgress.find(progress => progress.lesson_id === lessonId);
    console.log(`📊 Progress for lesson ${lessonId}:`, progress);
    return progress;
  };

  // Log final state before rendering
  console.log("🎨 Rendering LessonsGrid with:", {
    moduleId: pathId,
    lessonsCount: lessons.length,
    userProgressCount: userProgress.length,
    moduleTitle: moduleDetails?.title
  });

  // Show helpful message if no lessons found
  if (!lessons || lessons.length === 0) {
    return (
      <LessonsGridEmpty 
        onBack={onBack} 
        pathId={pathId} 
        moduleTitle={moduleDetails?.title} 
      />
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Back Button and Module Header */}
      <LessonsGridHeader 
        onBack={onBack}
        moduleDetails={moduleDetails}
        lessonsCount={lessons.length}
      />

      {/* Lessons Grid */}
      <div className="flex flex-col gap-6 max-w-2xl mx-auto px-8 relative">
        {/* Progress Path Line */}
        <div className="absolute left-[3.25rem] top-0 bottom-0 w-1 bg-gray-200 -z-10" />
        
        {lessons.map((lesson) => (
          <LessonCard
            key={lesson.id}
            lesson={lesson}
            isLocked={false}
            progress={getProgressForLesson(lesson.id)}
            previousLessonCompleted={true}
            onClick={() => {
              console.log("🚀 Starting lesson:", { 
                lessonId: lesson.id, 
                title: lesson.title,
                orderIndex: lesson.order_index,
                hasContent: !!lesson.content
              });
              onSelectLesson(lesson);
            }}
          />
        ))}
      </div>
    </div>
  );
};
